import { useMemo, useRef } from "react";

import type { Recording } from "@/types";
import { cn } from "@/lib/utils";


export interface ScrubberGap {
  /** ISO datetime when the gap began. */
  start: string;
  /** ISO datetime when the gap ended. */
  end: string;
  /** Optional label for tooltip / accessible name. */
  label?: string;
}


interface TimelineScrubberProps {
  /** UTC date to render — e.g. "2026-05-12". The scrubber draws the full
   *  24-hour band of that day. */
  date: string;
  recordings: Recording[];
  /** Currently playing recording id, or null. Used to render the active
   *  band differently from finished ones. */
  activeRecordingId?: string | null;
  /** Called when the user clicks a recording band. */
  onSelect?: (recording: Recording) => void;
  /** S8 — gap markers rendered as red bands behind the green recording
   *  bands. Optional; the scrubber works without gaps for backward
   *  compatibility with pre-S8 callers. */
  gaps?: ScrubberGap[];
  /** S8 — invoked when a gap band is clicked (e.g. open the acknowledge
   *  panel). Optional; no-op when omitted. */
  onSelectGap?: (gap: ScrubberGap) => void;
  className?: string;
}


const HOURS = Array.from({ length: 25 }, (_, i) => i); // 0..24 inclusive


/** 24-hour horizontal scrubber. Orange segments mark windows where
 *  recordings exist; click a segment to seek the player to that recording.
 *  No drag-to-scrub in v1 — the player itself owns the in-segment
 *  position; the timeline is for jumping *between* recordings. */
export function TimelineScrubber({
  date,
  recordings,
  activeRecordingId,
  onSelect,
  gaps,
  onSelectGap,
  className,
}: TimelineScrubberProps) {
  const dayStartMs = useMemo(() => Date.parse(`${date}T00:00:00Z`), [date]);
  const ref = useRef<HTMLDivElement>(null);

  const bands = useMemo(() => {
    if (Number.isNaN(dayStartMs)) return [];
    const dayEndMs = dayStartMs + 24 * 60 * 60 * 1000;
    return recordings
      .map((r) => {
        const start = Date.parse(r.started_at);
        const end = r.ended_at ? Date.parse(r.ended_at) : start + 60 * 60 * 1000;
        if (Number.isNaN(start) || end <= dayStartMs || start >= dayEndMs) {
          return null;
        }
        const clampedStart = Math.max(start, dayStartMs);
        const clampedEnd = Math.min(end, dayEndMs);
        const leftPct = ((clampedStart - dayStartMs) / (24 * 3600_000)) * 100;
        const widthPct = ((clampedEnd - clampedStart) / (24 * 3600_000)) * 100;
        return { recording: r, leftPct, widthPct };
      })
      .filter(
        (x): x is { recording: Recording; leftPct: number; widthPct: number } =>
          x !== null,
      );
  }, [recordings, dayStartMs]);

  // S8 — gap bands. Computed in the same coordinate space so they line
  // up with the recording bands above. Rendered behind them.
  const gapBands = useMemo(() => {
    if (!gaps || gaps.length === 0 || Number.isNaN(dayStartMs)) return [];
    const dayEndMs = dayStartMs + 24 * 60 * 60 * 1000;
    return gaps
      .map((g) => {
        const start = Date.parse(g.start);
        const end = Date.parse(g.end);
        if (
          Number.isNaN(start) || Number.isNaN(end)
          || end <= dayStartMs || start >= dayEndMs
        ) {
          return null;
        }
        const clampedStart = Math.max(start, dayStartMs);
        const clampedEnd = Math.min(end, dayEndMs);
        const leftPct = ((clampedStart - dayStartMs) / (24 * 3600_000)) * 100;
        const widthPct = ((clampedEnd - clampedStart) / (24 * 3600_000)) * 100;
        return { gap: g, leftPct, widthPct };
      })
      .filter(
        (x): x is { gap: ScrubberGap; leftPct: number; widthPct: number } =>
          x !== null,
      );
  }, [gaps, dayStartMs]);

  return (
    <div className={cn("w-full", className)}>
      <div
        ref={ref}
        className="relative h-12 w-full overflow-hidden rounded border bg-muted/40"
      >
        {/* Hour grid */}
        <div className="absolute inset-0 flex">
          {HOURS.slice(0, 24).map((h) => (
            <div
              key={h}
              className="flex-1 border-r border-border/40 last:border-r-0"
              aria-hidden
            />
          ))}
        </div>
        {/* S8 — Gap bands. Rendered first so the recording bands paint
            on top when they happen to overlap (defense against bad
            data). */}
        {gapBands.map(({ gap, leftPct, widthPct }) => (
          <button
            key={`gap-${gap.start}-${gap.end}`}
            type="button"
            onClick={() => onSelectGap?.(gap)}
            title={gap.label ?? `Gap: ${gap.start} → ${gap.end}`}
            className="absolute top-1 bottom-1 rounded-sm bg-red-500/60 transition-opacity hover:bg-red-500/80"
            style={{
              left: `${leftPct}%`,
              width: `${Math.max(widthPct, 0.3)}%`,
            }}
            aria-label={`Recording gap from ${gap.start} to ${gap.end}`}
          />
        ))}
        {/* Recording bands */}
        {bands.map(({ recording, leftPct, widthPct }) => {
          const isActive = recording.id === activeRecordingId;
          return (
            <button
              key={recording.id}
              type="button"
              onClick={() => onSelect?.(recording)}
              title={`${recording.started_at} → ${recording.ended_at ?? "live"}`}
              className={cn(
                "absolute top-1 bottom-1 rounded-sm transition-opacity hover:opacity-100",
                isActive
                  ? "bg-[#ff6d00] opacity-100 ring-2 ring-[#ff6d00]/40"
                  : "bg-[#ff6d00]/70 opacity-90",
              )}
              style={{
                left: `${leftPct}%`,
                width: `${Math.max(widthPct, 0.3)}%`,
              }}
              aria-label={`Recording starting at ${recording.started_at}`}
            />
          );
        })}
      </div>
      {/* Hour labels */}
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
        {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((h) => (
          <span key={h}>{String(h).padStart(2, "0")}:00</span>
        ))}
      </div>
    </div>
  );
}
